import React, { useEffect, useState } from 'react';
import CallRecordsResource from 'flow/api/callrecords';
import { fromJS } from 'immutable';
import { homeActionUrlPath } from '../utils';
import AudioPlayer from 'flow/components/AudioPlayer';
import CallRecordingPageItem from '../components/custom-components/CallRecordingPageItem';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import BrandLogo from 'flow/components/nav/BrandLogo';
import MuiSnackbar from 'flow/components/Snackbar';
import useAppContext from 'flow/hooks/useAppContext';
import { fetchBrandingLogoUrls } from 'flow/actions/brandingV2-actions';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import { CallParams } from 'flow/types/CallParams';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'flow/hooks/useRedux';

const CallRecordingPage = () => {
    const { callRecordKey } = useParams<CallParams>();
    const { i18next } = useAppContext();

    const [callRecord, setCallRecord] = useState(null);
    const [callRecordError, setCallRecordError] = useState(null);
    const [brandingReady, setBrandingReady] = useState(false);

    const ownCountryCode = useAppSelector(getOwnCountryCode);

    useEffect(() => {
        fetchBrandingLogoUrls().then(() => setBrandingReady(true));
    }, []);

    useEffect(() => {
        CallRecordsResource.getCallRecord(
            {
                onResult(result: any) {
                    setCallRecord(fromJS(result));
                },
                onError: () => {
                    setCallRecordError('networkError');
                },
                onClientError: () => {
                    setCallRecordError('clientError');
                },
            },
            callRecordKey,
            ['recordings'],
        );
    }, [callRecordKey]);

    const getInfoBoxText = () => {
        if (callRecord) {
            if (callRecord.get('recording')) {
                return i18next.t('call_recording_processing');
            }
            return i18next.t('no_call_recording_for_call');
        }
        if (callRecordError) {
            return i18next.t('no_call_recording_found');
        }
        return null;
    };

    const phoneNumber = callRecord
        ? callRecord.getIn(['number', 'number'])
        : 'unknown';
    const phoneNumberPrettyFormatted = prettyFormatE164Number(
        phoneNumber,
        ownCountryCode,
    );
    const infoBoxStatusText = getInfoBoxText();
    const errorType = {
        networkError: 'networkError',
        clientError: 'clientError',
    };

    return (
        <div className="flex size-full items-center justify-center">
            <div className="m-6 flex w-[440px] flex-col">
                {brandingReady && <BrandLogo type="logo48" />}

                <div className="mt-6 mb-4 flex font-bold">
                    {i18next.t('callrecord')}
                </div>

                <div>
                    {callRecord?.get('key') && (
                        <CallRecordingPageItem
                            callRecordKey={callRecord.get('key')}
                            callRecordNumberPrettyFormatted={
                                phoneNumberPrettyFormatted
                            }
                        />
                    )}
                    {callRecord?.get('recording') && (
                        <div className="flex flex-row items-center gap-2">
                            <AudioPlayer
                                uniqueId={callRecord.getIn([
                                    'voiceMessage',
                                    'uniqueId',
                                ])}
                                duration={callRecord.getIn([
                                    'recording',
                                    'durationSeconds',
                                ])}
                                url={homeActionUrlPath(
                                    callRecord.getIn([
                                        'recording',
                                        'playbackURL',
                                    ]),
                                )}
                            />

                            <a
                                href={homeActionUrlPath(
                                    callRecord.getIn([
                                        'recording',
                                        'downloadURL',
                                    ]),
                                )}
                                download={true}
                            >
                                <IconButton
                                    tooltipLabel={i18next.t(
                                        'Tooltip.Audio.Download',
                                    )}
                                    className="rounded bg-gray-200"
                                >
                                    <Icon
                                        iconKey={'download'}
                                        className="text-gray-600"
                                    />
                                </IconButton>
                            </a>
                        </div>
                    )}
                    <div className="py-2">
                        <InfoBox type={InfoBoxType.Info}>
                            {infoBoxStatusText}
                        </InfoBox>
                    </div>

                    {callRecordError === errorType.networkError && (
                        <MuiSnackbar
                            open={true}
                            message={i18next.t('snackbar.error')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallRecordingPage;
